import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS, PHONES, EMAIL } from "@/lib/site";
import { EASE } from "@/components/Reveal";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 48));

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-cream border-b border-line ${
          scrolled ? "shadow-[0_4px_20px_rgba(22,25,28,0.10)]" : "shadow-[0_2px_12px_rgba(22,25,28,0.06)]"
        }`}
        data-testid="site-navbar"
      >
        <div className="max-w-[1440px] mx-auto flex items-center justify-between px-5 sm:px-8 lg:px-12 h-[72px]">
          <a href="#home" aria-label="ArcView Homes — home" data-testid="nav-logo-link">
            <Logo />
          </a>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.replace("#", "")}`}
                className="text-[13px] font-semibold tracking-wide text-charcoal hover:text-arc transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-arc after:transition-all after:duration-300 hover:after:w-full"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="#contact"
              data-testid="nav-cta-start-project"
              className="hidden sm:inline-flex items-center gap-2 bg-arc text-cream text-[13px] font-semibold tracking-wide px-6 py-3 rounded-full hover:bg-arc-dark hover:shadow-lg hover:shadow-arc/25 transition-all duration-300"
            >
              Start Your Project
            </a>
            <button
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="nav-hamburger-button"
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-charcoal/20 text-charcoal"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-[60] bg-ink text-cream grain flex flex-col"
            data-testid="mobile-menu-overlay"
          >
            <div className="flex items-center justify-between px-5 sm:px-8 h-[72px] border-b border-white/10">
              <Logo dark />
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close-button"
                className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/25"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 sm:px-10 py-8 flex flex-col justify-center" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.href.replace("#", "")}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: EASE, delay: 0.08 + i * 0.05 }}
                  className="font-serif text-4xl py-3 border-b border-white/10 flex items-baseline justify-between group"
                >
                  {l.label}
                  <span className="font-mono text-[10px] tracking-[0.3em] text-cream/40">0{i + 1}</span>
                </motion.a>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE, delay: 0.55 }}
                className="mt-8 space-y-3"
              >
                <a
                  href="#contact"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-menu-enquire-button"
                  className="block w-full text-center bg-arc text-cream font-semibold py-4 rounded-full"
                >
                  Enquire Now
                </a>
                {PHONES.map((p) => (
                  <a
                    key={p.tel}
                    href={`tel:${p.tel}`}
                    data-testid={`mobile-menu-phone-${p.tel}`}
                    className="flex items-center gap-3 text-cream/80 py-1"
                  >
                    <Phone size={15} className="text-sage" /> {p.display}
                  </a>
                ))}
                <a href={`mailto:${EMAIL}`} data-testid="mobile-menu-email" className="block text-cream/60 text-sm">
                  {EMAIL}
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
